import React, { useCallback } from "react";
import { Alert, AlertTitle, Button, Paper, Typography } from "@mui/material";

import { apiSlice } from "fond/api";
import { useCustomWorkflow } from "fond/hooks/useCustomWorkflow";
import { loadProject } from "fond/project/redux";
import { Project } from "fond/types";
import { useAppDispatch } from "fond/utils/hooks";
import LinearProgressWithLabel from "fond/widgets/LinearProgressWithLabel";

interface ArtefactsPanelProps {
  project: Project;
  versionId: string;
  readOnly: boolean;
}

const ServiceAreaPanel: React.FC<ArtefactsPanelProps> = ({ project, versionId, readOnly = false }: ArtefactsPanelProps) => {
  const dispatch = useAppDispatch();
  const handleOnComplete = useCallback(async () => {
    await dispatch(apiSlice.util.invalidateTags([{ type: "VersionRootConfiguration", id: versionId }]));
    await dispatch(loadProject({ uuid: project.ID, forceRefetch: true }));
  }, [dispatch, project.ID, versionId]);

  const { workflowState, workflowProgress, runWorkflow } = useCustomWorkflow({
    versionId,
    key: "planner_extended_service_area",
    onComplete: handleOnComplete,
  });
  const isGenerating = workflowState === "STARTING" || workflowState === "IN_PROGRESS";

  const handleGenerateServiceArea = async () => {
    runWorkflow?.();
  };
  return (
    <>
      <Typography variant="body3" textAlign="center">
        Break your design into sections for easier analysis.
      </Typography>
      <Button
        fullWidth
        variant="contained"
        size="small"
        sx={{ mt: 2 }}
        disabled={!runWorkflow || isGenerating || readOnly}
        onClick={handleGenerateServiceArea}
      >
        Generate sections
      </Button>
      {isGenerating && (
        <Paper variant="outlined" sx={{ py: 2, px: 2.75, mt: 2 }}>
          <LinearProgressWithLabel
            color="primary"
            value={(workflowProgress ?? 0) * 100}
            progressBarMarginRight={0}
            title="Generating"
            subtext="Processing..."
          />
        </Paper>
      )}
      {workflowState === "ERROR" && (
        <Alert severity="error" sx={{ mt: 4 }}>
          <AlertTitle>Generation failed</AlertTitle>
          Data could not be generated.
        </Alert>
      )}
    </>
  );
};

export default ServiceAreaPanel;
